import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { trackEvent } from '../lib/analytics';

function BillingSuccessPage({ session }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [error, setError] = useState('');

  const sessionId = searchParams.get('session_id');
  const hasTrackedCompletionRef = useRef(false);

  useEffect(() => {
    trackEvent('checkout_success_page_opened', {
      has_session_id: Boolean(sessionId),
    });
  }, [sessionId]);

  useEffect(() => {
    let pollTimer;
    let attempts = 0;
    const maxAttempts = 10;

    const checkProfile = async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('plan_status')
          .eq('id', session.user.id)
          .single();

        if (error) {
          throw error;
        }

        if (data?.plan_status === 'active') {
          setStatus('active');

          if (!hasTrackedCompletionRef.current) {
            hasTrackedCompletionRef.current = true;
            trackEvent('checkout_completed', {
              source: 'billing_success_page',
            });
          }

          return;
        }

        attempts += 1;

        if (attempts >= maxAttempts) {
          setStatus('pending');
          trackEvent('checkout_pending', {
            source: 'billing_success_page',
          });
          return;
        }

        pollTimer = setTimeout(checkProfile, 2000);
      } catch (err) {
        console.error(err);
        setError('We received your return from Stripe, but could not verify your plan yet.');
        setStatus('pending');
        trackEvent('checkout_verification_error', {
          source: 'billing_success_page',
        });
      }
    };

    checkProfile();

    return () => {
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [session.user.id]);

  return (
    <div className="app dark mobile-safe-page">
      <main className="billing-success-page">
        <div className="billing-success-card">
          <div className="pricing-badge">Billing</div>

          {status === 'checking' && (
            <>
              <h1 className="billing-success-title">Finalizing your upgrade...</h1>
              <p className="billing-success-text">
                Your payment was submitted successfully. We’re confirming your Pro access now.
              </p>
              {sessionId && (
                <p className="billing-success-meta">Session: {sessionId}</p>
              )}
            </>
          )}

          {status === 'active' && (
            <>
              <h1 className="billing-success-title">You’re now Pro</h1>
              <p className="billing-success-text">
                Your account has been upgraded successfully. Unlimited analyses are now enabled.
              </p>
              <button
                type="button"
                className="upgrade-btn primary pricing-cta"
                onClick={() => navigate('/')}
              >
                Go to Analyzer
              </button>
            </>
          )}

          {status === 'pending' && (
            <>
              <h1 className="billing-success-title">Payment received</h1>
              <p className="billing-success-text">
                We received your return from Stripe. Your plan may still be updating in the background.
                Please refresh the analyzer in a moment.
              </p>
              <button
                type="button"
                className="upgrade-btn primary pricing-cta"
                onClick={() => navigate('/')}
              >
                Return to Analyzer
              </button>
            </>
          )}

          {error && <p className="error">{error}</p>}
        </div>
      </main>
    </div>
  );
}

export default BillingSuccessPage;